import type { ConfigurationTestResultId } from "@repo/app-types/entities/configurationTestResult";
import type { CopilotProposalId } from "@repo/app-types/entities/copilotProposal";
import {
  allStaticAgentInstructionTileKeys,
  compareStaticSectionValuesAsStored,
  STATIC_AGENT_INSTRUCTION_TILES,
  type StaticAgentInstructionTileKey,
  type StaticTeammateAgentSections,
} from "@repo/app-types/entities/teammateAgent";
import type { TeammateAgentInstructionProposalData } from "@repo/app-types/entities/teammateAgentProposal";
import type {
  AgentInstructionChecksLlmOutput,
  StaticAgentSectionKey,
} from "@repo/app-types/entities/voiceAgentConfigurationTools";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { useEffectEvent } from "@repo/ui/helpers/useEffectEvent";
import { Input } from "@repo/ui/Inputs/input";
import { Textarea } from "@repo/ui/Inputs/textarea";
import { Label } from "@repo/ui/Label/label";
import { linkStyle } from "@repo/ui/Link/index";
import { InfoIconTooltip } from "@repo/ui/tooltip/infoIconTooltip";
import {
  IconCircles,
  IconCube3dSphere,
  IconMessageChatbot,
  IconSparkles,
  IconSTurnRight,
  IconUserHexagon,
} from "@tabler/icons-react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type FC,
} from "react";
import { useNavigate } from "react-router";
import { twMerge } from "tailwind-merge";
import { useResolveProposalMutation } from "../../../api/entities/copilotProposal.js";
import { useGetTeammateAgentQuery } from "../../../api/entities/teammateAgent.js";
import { isConflictQueryError } from "../../../api/queryErrors.js";
import { FieldDiffView } from "../../../components/recordingAssessmentEditor/fieldDiffView.js";
import { NodeChangeDot } from "../../../components/recordingAssessmentEditor/nodeChangeDot.js";
import { useAppDispatch } from "../../../hooks/hook.js";
import { useResearchAgentProps } from "../../copilot/copilotContext.js";
import {
  useDeepLinkedProposal,
  useDeepLinkedTestResult,
} from "../../copilot/copilotDeepLinks.js";
import { buildTestInstructionsPrompt } from "../../copilot/copilotRouteRegistry.js";
import {
  useReportEditorProposal,
  type EditorFocus,
} from "../../copilot/editorProposalsSlice.js";
import {
  NotificationType,
  showNotification,
} from "../../notificationsOverlay/notificationsSlice.js";
import { AgentSectionChecksCard } from "./agentSectionChecksCard.js";
import { AvailableResourcesCard } from "./availableResourcesCard.js";
import { useStaticAgentConfig } from "./staticAgentConfigContext.js";

const TILE_ICONS: Record<
  StaticAgentInstructionTileKey,
  ComponentType<{ className?: string }>
> = {
  identity: IconCircles,
  caller_context: IconUserHexagon,
  conversation_flow: IconSTurnRight,
  behavior_and_guardrails: IconCube3dSphere,
  outreach_instructions: IconMessageChatbot,
};

const OUTREACH_PLACEHOLDER = `How to run the outreach over time. Example:

Check in with customers before their renewal. Be brief and warm.

1. Text first: "<the approved message>". Wait for a reply.
2. If they reply that all is well, thank them and finish.
3. If they need help, text a confirmation, then call them about five minutes later.
4. If there is no reply, finish when the task expires.

Say what to text word for word where it matters, when a call is warranted, and when to stop.`;

const ROLE_PLACEHOLDER =
  "Describe what the agent does, how it should sound, and any persona boundaries it should follow.";
const CALLER_CONTEXT_PLACEHOLDER =
  "A potential customer calling the phone number on our website.";
const CONVERSATION_FLOW_PLACEHOLDER = `Follow this conversation flow unless the caller clearly needs something different.

## 1. Opening
Greet the caller, introduce yourself, and ask how you can help.

## 2. Understand the request
Clarify what the caller is trying to accomplish before giving an answer.

## 3. Resolve or route
Help the caller complete the next step when possible, or transfer/escalate according to the available options.

## 4. Close
Summarize the outcome, confirm any next step, and ask if there is anything else they need.`;
const GUARDRAILS_PLACEHOLDER =
  "Add any general behavior rules and guardrails the agent should always follow.";

// Match the HQ section authoring limits.
const NAME_MAX_LENGTH = 200;
const SECTION_MAX_LENGTH = 100000;

type FieldTab = "edit" | "diff";

/**
 * A labeled identity-section field with a "See changes" toggle that diffs the
 * current value against the published (live) value, mirroring the Outcome and
 * Summary template editors.
 */
const SectionFieldEditor: FC<{
  label: string;
  /** Optional help text shown in a tooltip on an info icon next to the label. */
  labelTooltip?: string;
  value: string;
  /** Published value to diff against; null when live isn't loaded. */
  liveValue: string | null;
  onChange: (value: string) => void;
  placeholder: string;
  isMultiline: boolean;
  isDisabled: boolean;
  fillHeight?: boolean;
  maxLength?: number;
  /**
   * The copilot's proposed replacement for this field, if any. When set, the
   * field renders read-only as a diff against the proposal; Apply/Discard live
   * on the section-level banner (see `InstructionsTab`).
   */
  suggestion?: string;
}> = ({
  label,
  labelTooltip,
  value,
  liveValue,
  onChange,
  placeholder,
  isMultiline,
  isDisabled,
  fillHeight = false,
  maxLength,
  suggestion,
}) => {
  const [tab, setTab] = useState<FieldTab>("edit");
  const showDiffToggle =
    liveValue !== null && !compareStaticSectionValuesAsStored(liveValue, value);
  const isDiffMode = showDiffToggle && tab === "diff";
  // A pending proposal takes over the field: the current value is diffed
  // against the proposal (read-only). The Apply/Discard banner wraps the whole
  // section (see `InstructionsTab`), mirroring the playbook editor.
  const isSuggestionMode = suggestion !== undefined;

  // Once the value matches live again, leave diff mode so the next edit lands
  // in the editor rather than the read-only diff view.
  useEffect(() => {
    if (!showDiffToggle && tab !== "edit") {
      setTab("edit");
    }
  }, [showDiffToggle, tab]);

  return (
    <div
      className={twMerge(
        "flex flex-col",
        fillHeight ? "min-h-0 flex-1" : "shrink-0",
      )}
    >
      <div className="mb-1.5 flex min-h-8 items-center justify-between">
        <div className="flex items-center gap-1">
          <Label size={Size.SMALL}>{label}</Label>
          {labelTooltip ? (
            <InfoIconTooltip>
              <span className="block max-w-xs">{labelTooltip}</span>
            </InfoIconTooltip>
          ) : null}
        </div>
        {!isSuggestionMode && showDiffToggle ? (
          <Button
            variant={ButtonVariant.Tertiary}
            size={Size.MEDIUM}
            className={twMerge("cursor-pointer pr-0", linkStyle())}
            onClick={() => setTab(tab === "edit" ? "diff" : "edit")}
          >
            {tab === "edit" ? "See changes" : "Edit"}
          </Button>
        ) : null}
      </div>
      {isSuggestionMode ? (
        <FieldDiffView
          className={fillHeight ? "min-h-0 flex-1 overflow-y-auto" : undefined}
          savedValue={value}
          editedValue={suggestion}
        />
      ) : isDiffMode ? (
        <FieldDiffView
          className={fillHeight ? "min-h-0 flex-1 overflow-y-auto" : undefined}
          savedValue={liveValue ?? ""}
          editedValue={value}
        />
      ) : isMultiline ? (
        <Textarea
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          isDisabled={isDisabled}
          fillHeight={fillHeight}
          maxLength={maxLength}
        />
      ) : (
        <Input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          isDisabled={isDisabled}
          maxLength={maxLength}
        />
      )}
    </div>
  );
};

/** The pending instruction proposal the tab shows on its tile. */
interface LinkedInstructionProposal {
  id: CopilotProposalId;
  data: TeammateAgentInstructionProposalData;
}

/** The proposal's new content per section it changes, keyed like the draft. */
function readProposedSections(
  proposal: LinkedInstructionProposal,
): Partial<Record<StaticAgentSectionKey, string>> {
  const proposed: Partial<Record<StaticAgentSectionKey, string>> = {};
  for (const section of STATIC_AGENT_INSTRUCTION_TILES[proposal.data.tile]
    .sections) {
    const change = proposal.data.sections[section];
    if (change) {
      proposed[section] = change.content;
    }
  }
  return proposed;
}

/**
 * The Instructions tab for a static agent: a left sub-navigation over the
 * authored prompt sections, an editor for the selected section, and a
 * read-only summary of attached resources. Section edits autosave to the draft
 * via the shared controller and go live through the toolbar's Publish button.
 *
 * An Ask Alpharun row deep-links here with `?proposalId=` or `?testResultId=`
 * (both one-shot): a pending proposal shows on its tile as a read-only diff
 * with Apply / Discard, and a test result fills the Results panel.
 */
export const InstructionsTab: FC = () => {
  const {
    teammateAgentId,
    agentConfig,
    flushAgentConfigSave,
    hasUnpublishedChanges,
  } = useStaticAgentConfig();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const researchAgent = useResearchAgentProps();
  const { data: teammateAgent } = useGetTeammateAgentQuery(teammateAgentId);
  const [resolveProposal, { isLoading: isResolvingProposal }] =
    useResolveProposalMutation();

  const [selectedKey, setSelectedKey] =
    useState<StaticAgentInstructionTileKey>("identity");

  // `sections` is a single nested object on the config draft. Until the draft
  // query resolves it is null; editing then would write a mostly-empty object
  // back and wipe the four sections the user didn't touch, so gate editing on
  // load rather than falling back to an empty object.
  const sections = agentConfig.data?.sections ?? null;
  const isDisabled = sections === null;

  // The proposal the tab is showing, the checks of the last proposal or test
  // result it loaded, and the test result the Results panel is showing.
  const [linkedProposal, setLinkedProposal] =
    useState<LinkedInstructionProposal | null>(null);
  const [checks, setChecks] = useState<AgentInstructionChecksLlmOutput | null>(
    null,
  );
  const [linkedTestResultId, setLinkedTestResultId] = useState<
    ConfigurationTestResultId | undefined
  >();

  const notifyUnavailable = useEffectEvent((subject: string) => {
    showNotification(dispatch, {
      id: `instructions-link-unavailable-${Date.now().toString()}`,
      type: NotificationType.FAILURE,
      primaryMessage: `This ${subject} is no longer available.`,
    });
  });

  // `?proposalId=`: the pending row goes onto its tile as soon as it loads. A
  // row for another agent, or one that is not an instruction proposal, loads
  // nothing.
  const { row: deepLinkProposal, clear: clearDeepLinkProposalId } =
    useDeepLinkedProposal("teammateAgent", {
      onUnavailable: () => notifyUnavailable("suggestion"),
    });
  useEffect(() => {
    if (!deepLinkProposal) {
      return;
    }
    clearDeepLinkProposalId();
    if (
      deepLinkProposal.resource_id !== teammateAgentId ||
      deepLinkProposal.data.kind !== "update_instruction_section"
    ) {
      return;
    }
    const { data } = deepLinkProposal;
    setLinkedProposal({ id: deepLinkProposal.id, data });
    setLinkedTestResultId(undefined);
    setChecks(data.checks);
    setSelectedKey(data.tile);
  }, [deepLinkProposal, teammateAgentId, clearDeepLinkProposalId]);

  // `?testResultId=`: the saved checks fill the Results panel.
  const { row: deepLinkTestResult, clear: clearDeepLinkTestResultId } =
    useDeepLinkedTestResult("teammateAgent", {
      onUnavailable: () => notifyUnavailable("test result"),
    });
  useEffect(() => {
    if (!deepLinkTestResult) {
      return;
    }
    clearDeepLinkTestResultId();
    if (deepLinkTestResult.resource_id !== teammateAgentId) {
      return;
    }
    setLinkedTestResultId(deepLinkTestResult.id);
    setChecks(deepLinkTestResult.data.checks);
  }, [deepLinkTestResult, teammateAgentId, clearDeepLinkTestResultId]);

  // Publishing or discarding the draft discards the pending proposals server
  // side and replaces what the checks measured. The flag goes `true → false`
  // only on a successful publish/discard; edits and failed publishes keep it
  // `true`, so this fires exactly on settle.
  const prevHasUnpublishedRef = useRef(hasUnpublishedChanges);
  useEffect(() => {
    const justSettled = prevHasUnpublishedRef.current && !hasUnpublishedChanges;
    prevHasUnpublishedRef.current = hasUnpublishedChanges;
    if (justSettled) {
      setLinkedProposal(null);
      setLinkedTestResultId(undefined);
      setChecks(null);
    }
  }, [hasUnpublishedChanges]);

  // Tell the chat which row this tab is showing and which tile is selected.
  const hasUnsavedChanges = agentConfig.hasUnsavedChanges;
  const editorFocus = useMemo(
    (): EditorFocus => ({
      resource_type: "teammateAgent",
      teammateAgentId,
      tile: selectedKey,
      hasUnsavedChanges,
    }),
    [teammateAgentId, selectedKey, hasUnsavedChanges],
  );
  useReportEditorProposal(
    linkedProposal?.id ?? linkedTestResultId,
    isResolvingProposal,
    editorFocus,
  );

  const resolveLinkedProposal = async (
    proposal: LinkedInstructionProposal,
    status: "applied" | "discarded",
  ) => {
    try {
      await resolveProposal({ proposalId: proposal.id, status }).unwrap();
    } catch (err) {
      if (isConflictQueryError(err)) {
        notifyUnavailable("suggestion");
      } else {
        console.error("Failed to resolve the suggestion:", err);
        showNotification(dispatch, {
          id: `instructions-resolve-error-${Date.now().toString()}`,
          type: NotificationType.FAILURE,
          primaryMessage: "Failed to update the suggestion",
          secondaryMessage: "Please try again.",
        });
        return;
      }
    }
    setLinkedProposal((current) => (current === proposal ? null : current));
  };

  // Applying commits the proposed content to the draft, then — once the
  // change has rendered, which the flush reads — persists the draft and
  // records the decision. A failed flush leaves the proposal pending, so the
  // next attempt retries it.
  const [applyingProposal, setApplyingProposal] =
    useState<LinkedInstructionProposal | null>(null);
  const applyLinkedProposal = () => {
    if (!sections || !linkedProposal) {
      return;
    }
    agentConfig.onChange({
      sections: {
        ...sections,
        ...readProposedSections(linkedProposal),
      },
    });
    setApplyingProposal(linkedProposal);
  };
  const finishApplyingProposal = useEffectEvent(
    async (proposal: LinkedInstructionProposal) => {
      const saved = await flushAgentConfigSave();
      setApplyingProposal(null);
      if (saved) {
        await resolveLinkedProposal(proposal, "applied");
      }
    },
  );
  useEffect(() => {
    if (applyingProposal) {
      void finishApplyingProposal(applyingProposal);
    }
  }, [applyingProposal, finishApplyingProposal]);

  const discardLinkedProposal = () => {
    if (linkedProposal) {
      void resolveLinkedProposal(linkedProposal, "discarded");
    }
  };

  // The Test button hands off to Ask Alpharun: the saved draft is what its
  // tool checks, and the turn's "Test results" row opens here. Outside the
  // Ask Alpharun shell (stories, tests) there is nothing to hand off to.
  const isAskAlpharunAvailable = researchAgent !== undefined;
  const [isFlushingForTest, setIsFlushingForTest] = useState(false);
  const runChecks = async () => {
    if (!researchAgent) {
      return;
    }
    setIsFlushingForTest(true);
    try {
      if (!(await flushAgentConfigSave())) {
        return;
      }
      researchAgent.onOpenChat();
      researchAgent.onSendMessage(
        buildTestInstructionsPrompt(teammateAgent?.name ?? "this agent"),
      );
    } finally {
      setIsFlushingForTest(false);
    }
  };

  // A section is "in a draft state" when its current value differs from the
  // published (live) value — the same unpublished-change signal the playbook
  // editor shows on its section tiles.
  const liveSections = agentConfig.liveData?.sections ?? null;
  const hasUnpublishedChange = (
    fields: readonly (keyof StaticTeammateAgentSections)[],
  ) =>
    sections !== null &&
    liveSections !== null &&
    fields.some(
      (field) =>
        !compareStaticSectionValuesAsStored(
          sections[field] ?? "",
          liveSections[field] ?? "",
        ),
    );

  const suggestions = linkedProposal
    ? readProposedSections(linkedProposal)
    : {};

  const updateSection = (
    key: keyof StaticTeammateAgentSections,
    value: string,
  ) => {
    if (!sections) {
      return;
    }
    agentConfig.onChange({ sections: { ...sections, [key]: value } });
  };

  // The Outreach tile is shown only for an agent that runs its own call tasks.
  const isAgentManaged =
    agentConfig.data?.settings.calling?.agent_managed ?? false;
  const navTileKeys = allStaticAgentInstructionTileKeys.filter(
    (tileKey) => tileKey !== "outreach_instructions" || isAgentManaged,
  );
  const activeKey = navTileKeys.includes(selectedKey)
    ? selectedKey
    : "identity";
  const activeTile = STATIC_AGENT_INSTRUCTION_TILES[activeKey];

  // The suggestion banner wraps the whole active tile (matching the playbook
  // editor), so Apply/Discard act on the proposal's every section.
  const activeSectionHasSuggestion = linkedProposal?.data.tile === activeKey;

  // While a section has a pending suggestion, the whole section is a read-only
  // review view (matching the playbook editor's `isReadOnly={isSuggestionMode}`):
  // every field is disabled and the suggested one shows the inline diff.
  const fieldsReadOnly = isDisabled || activeSectionHasSuggestion;

  return (
    <div className="flex h-full min-w-0 gap-2 bg-card p-2">
      <div className="flex w-[280px] shrink-0 flex-col justify-between gap-4 overflow-y-auto">
        <nav className="flex flex-col gap-2">
          {navTileKeys.map((tileKey) => {
            const tile = STATIC_AGENT_INSTRUCTION_TILES[tileKey];
            const isActive = tileKey === activeKey;
            const ItemIcon = TILE_ICONS[tileKey];
            return (
              <button
                key={tileKey}
                type="button"
                onClick={() => setSelectedKey(tileKey)}
                className={twMerge(
                  "flex w-full items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left text-sm font-semibold text-foreground",
                  isActive
                    ? "border-blue-200 bg-blue-100"
                    : "border-border-light bg-white hover:bg-blue-50",
                )}
              >
                <ItemIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate">{tile.label}</span>
                {linkedProposal?.data.tile === tileKey ? (
                  <NodeChangeDot
                    entity="section"
                    isVisible
                    variant="suggestion"
                  />
                ) : (
                  <NodeChangeDot
                    entity="section"
                    isVisible={hasUnpublishedChange(tile.sections)}
                  />
                )}
              </button>
            );
          })}
        </nav>

        <AvailableResourcesCard
          onEdit={() => navigate("../resources-outputs")}
        />
      </div>

      <div
        className={twMerge(
          "flex min-w-0 flex-1 flex-col overflow-hidden rounded-lg border bg-white px-2 py-2",
          // Highlight the card border when the section has a pending
          // suggestion, matching the playbook editor's settings pane.
          activeSectionHasSuggestion
            ? "border-indigo-500"
            : "border-border-light",
        )}
      >
        {/* Section-level suggestion banner — the rounded-top header of the
            section card, mirroring the playbook editor's settings panes
            (`criteriaSettings.tsx`). */}
        {activeSectionHasSuggestion ? (
          <div className="-mx-2 -mt-2 flex shrink-0 items-center justify-between rounded-t-lg bg-indigo-50 px-3 py-2">
            <div className="flex items-center gap-2 text-sm text-indigo-900">
              <IconSparkles className="size-4 shrink-0 text-indigo-500" />
              <span>Showing changes suggested by Ask Alpharun.</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={ButtonVariant.Tertiary}
                size={Size.MEDIUM}
                onClick={discardLinkedProposal}
                isDisabled={isResolvingProposal || applyingProposal !== null}
              >
                Discard changes
              </Button>
              <Button
                variant={ButtonVariant.Primary}
                size={Size.MEDIUM}
                onClick={applyLinkedProposal}
                isDisabled={
                  isDisabled || isResolvingProposal || applyingProposal !== null
                }
              >
                Apply changes
              </Button>
            </div>
          </div>
        ) : null}
        <div className="flex min-h-0 flex-1 flex-col">
          <div
            className={twMerge(
              "grid min-h-0 flex-1 grid-cols-1 gap-x-2",
              checks !== null && "md:grid-cols-[1fr_1px_1fr]",
            )}
          >
            <div className="flex min-h-0 flex-col p-2">
              <div className="flex min-h-0 flex-1 flex-col gap-4">
                <h2 className="shrink-0 text-base font-semibold text-foreground">
                  {activeTile.label}
                </h2>
                {activeKey === "identity" ? (
                  <>
                    <SectionFieldEditor
                      label="Name"
                      value={sections?.agent_name ?? ""}
                      liveValue={liveSections?.agent_name ?? null}
                      onChange={(value) => updateSection("agent_name", value)}
                      placeholder="Sarah"
                      isMultiline={false}
                      isDisabled={fieldsReadOnly}
                      maxLength={NAME_MAX_LENGTH}
                      suggestion={suggestions.agent_name}
                    />
                    <SectionFieldEditor
                      label="Role and personality"
                      labelTooltip="Agents already use a warm, calm, precise, conversational tone, vary routine phrasing, and avoid mentioning internal instructions. Add only agent-specific role, responsibilities, voice, tone, and persona guidance here."
                      value={sections?.agent_role_and_personality ?? ""}
                      liveValue={
                        liveSections?.agent_role_and_personality ?? null
                      }
                      onChange={(value) =>
                        updateSection("agent_role_and_personality", value)
                      }
                      placeholder={ROLE_PLACEHOLDER}
                      isMultiline
                      isDisabled={fieldsReadOnly}
                      fillHeight
                      maxLength={SECTION_MAX_LENGTH}
                      suggestion={suggestions.agent_role_and_personality}
                    />
                  </>
                ) : null}

                {activeKey === "caller_context" ? (
                  <SectionFieldEditor
                    label="Instructions"
                    labelTooltip="Agents already ask for clarification when caller details are unclear, and avoid guessing missing information. Add only agent-specific context about who is calling and what they usually need help with."
                    value={sections?.caller_context ?? ""}
                    liveValue={liveSections?.caller_context ?? null}
                    onChange={(value) => updateSection("caller_context", value)}
                    placeholder={CALLER_CONTEXT_PLACEHOLDER}
                    isMultiline
                    isDisabled={fieldsReadOnly}
                    fillHeight
                    maxLength={SECTION_MAX_LENGTH}
                    suggestion={suggestions.caller_context}
                  />
                ) : null}

                {activeKey === "conversation_flow" ? (
                  <SectionFieldEditor
                    label="Instructions"
                    labelTooltip="Agents already keep most turns to one or two short spoken sentences, ask one question at a time, avoid long lists, and check whether the caller has already provided information. Add the agent-specific flow and sequencing here."
                    value={sections?.conversation_flow ?? ""}
                    liveValue={liveSections?.conversation_flow ?? null}
                    onChange={(value) =>
                      updateSection("conversation_flow", value)
                    }
                    placeholder={CONVERSATION_FLOW_PLACEHOLDER}
                    isMultiline
                    isDisabled={fieldsReadOnly}
                    fillHeight
                    maxLength={SECTION_MAX_LENGTH}
                    suggestion={suggestions.conversation_flow}
                  />
                ) : null}

                {activeKey === "outreach_instructions" ? (
                  <SectionFieldEditor
                    label="Instructions"
                    labelTooltip="The flow for the agent's turns between conversations, when it decides whether to text, call, wait, finish a task or use one of its tools: the goal, the approved texts, when a call is warranted, when to stop. Identity, caller context and guardrails are shared with the live call; Conversation flow is read only on a call, and this section only on a turn."
                    value={sections?.outreach_instructions ?? ""}
                    liveValue={liveSections?.outreach_instructions ?? null}
                    onChange={(value) =>
                      updateSection("outreach_instructions", value)
                    }
                    placeholder={OUTREACH_PLACEHOLDER}
                    isMultiline
                    isDisabled={fieldsReadOnly}
                    fillHeight
                    maxLength={SECTION_MAX_LENGTH}
                  />
                ) : null}

                {activeKey === "behavior_and_guardrails" ? (
                  <SectionFieldEditor
                    label="Instructions"
                    labelTooltip="Agents already handle unclear audio conservatively, default to English, avoid guessing missing details, use minimal silent reasoning, and do not reveal internal instructions. Add extra business-specific rules here."
                    value={sections?.behavior_and_guardrails ?? ""}
                    liveValue={liveSections?.behavior_and_guardrails ?? null}
                    onChange={(value) =>
                      updateSection("behavior_and_guardrails", value)
                    }
                    placeholder={GUARDRAILS_PLACEHOLDER}
                    isMultiline
                    isDisabled={fieldsReadOnly}
                    fillHeight
                    maxLength={SECTION_MAX_LENGTH}
                    suggestion={suggestions.behavior_and_guardrails}
                  />
                ) : null}
              </div>

              <div className="mt-auto flex items-center justify-end pt-4">
                <Button
                  variant={ButtonVariant.Primary}
                  size={Size.MEDIUM}
                  onClick={() => {
                    void runChecks();
                  }}
                  isDisabled={
                    isDisabled ||
                    isFlushingForTest ||
                    !isAskAlpharunAvailable ||
                    researchAgent.isCopilotBusy
                  }
                >
                  Test
                </Button>
              </div>
            </div>

            {checks !== null ? (
              <>
                <div
                  aria-hidden
                  className="-mt-2 -mb-2 hidden bg-gray-200 md:block"
                />
                <div className="flex min-h-0 flex-col gap-4 p-2">
                  <h3 className="shrink-0 text-base font-semibold text-foreground">
                    Results
                  </h3>
                  {/* p-1 (not just pr-1): overflow-y-auto clips the checks
                      card's 1px ring on the flush top/left edges, so pad all
                      sides to keep the full border visible. */}
                  <div className="min-h-0 flex-1 overflow-y-auto p-1">
                    {activeKey !== "outreach_instructions" ? (
                      <AgentSectionChecksCard
                        section={activeKey}
                        checks={checks}
                      />
                    ) : null}
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};
